import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { BarChart3, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import {
  DIRETORIA_DEMO_COMPANIES,
  DIRETORIA_DEMO_PERIODS,
  DIRETORIA_INDICATORS_MOCK,
  type DiretoriaIndicatorMock,
} from '@/data/diretoriaMock';

const PIE_COLORS = ['#0f172a', '#ca8a04', '#2563eb', '#16a34a', '#9333ea', '#ea580c'];

function DiretoriaChart({ indicator }: { indicator: DiretoriaIndicatorMock }) {
  const h = 260;
  if (indicator.chartKind === 'none') {
    return (
      <div className="flex h-[260px] items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 text-sm text-muted-foreground">
        Gráfico indisponível neste indicador — ver tabela e notas abaixo.
      </div>
    );
  }
  if (indicator.chartKind === 'pie' && indicator.pieData?.length) {
    return (
      <div style={{ height: h }} className="w-full min-h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={indicator.pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={88}
              label={({ name, percent }) => `${name} ${(((percent ?? 0) as number) * 100).toFixed(0)}%`}
            >
              {indicator.pieData.map((_, i) => (
                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(v: number) => [v, 'Qtd.']} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }
  const data = indicator.series ?? [];
  const key = indicator.barSeriesKey ?? 'valor';
  if (indicator.chartKind === 'bar') {
    return (
      <div style={{ height: h }} className="w-full min-h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={36} />
            <Tooltip />
            <Bar dataKey={key} fill="hsl(222 47% 18%)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }
  return (
    <div style={{ height: h }} className="w-full min-h-[260px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} width={36} />
          <Tooltip />
          <Line type="monotone" dataKey="valor" stroke="hsl(222 47% 18%)" strokeWidth={2} dot={false} name="Série A" />
          {data.some((d) => d.valor2 != null) ? (
            <Line type="monotone" dataKey="valor2" stroke="#ca8a04" strokeWidth={2} dot={false} name="Série B" />
          ) : null}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export const DiretoriaPage: React.FC = () => {
  const [companyId, setCompanyId] = useState<string>(DIRETORIA_DEMO_COMPANIES[0].id);
  const [periodId, setPeriodId] = useState<string>(DIRETORIA_DEMO_PERIODS[0].id);
  const [open, setOpen] = useState<DiretoriaIndicatorMock | null>(null);

  const demoSubtitle = useMemo(() => {
    const c = DIRETORIA_DEMO_COMPANIES.find((x) => x.id === companyId)?.label ?? '';
    const p = DIRETORIA_DEMO_PERIODS.find((x) => x.id === periodId)?.label ?? '';
    return `${c} · ${p}`;
  }, [companyId, periodId]);

  return (
    <>
      <Helmet>
        <title>Diretoria — PortalFly</title>
      </Helmet>

      <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-5">
        <div
          className="flex flex-col gap-3 rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-100 sm:flex-row sm:items-center sm:justify-between"
          role="status"
        >
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <p>
              <strong>Pré-visualização</strong> — dados de exemplo apenas. Nenhum valor abaixo reflete a produção.
              Fase seguinte: integração com API e regras de negócio consolidadas.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-1 flex items-center gap-2 text-muted-foreground">
              <BarChart3 className="h-5 w-5" aria-hidden />
              <span className="text-xs font-medium uppercase tracking-wide">Módulo</span>
            </div>
            <h1 className="font-heading text-heading-2 font-bold text-foreground">Diretoria</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Indicadores executivos — mockup de layout e análises. Os filtros abaixo são apenas visuais para
              simulação.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Empresa (demo)
              <select
                className={cn(
                  'h-9 min-w-[12rem] rounded-md border border-input bg-background px-2 text-sm text-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )}
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
              >
                {DIRETORIA_DEMO_COMPANIES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Período (demo)
              <select
                className={cn(
                  'h-9 min-w-[10rem] rounded-md border border-input bg-background px-2 text-sm text-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )}
                value={periodId}
                onChange={(e) => setPeriodId(e.target.value)}
              >
                {DIRETORIA_DEMO_PERIODS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">Cenário selecionado: {demoSubtitle}</p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {DIRETORIA_INDICATORS_MOCK.map((ind) => (
            <button
              key={ind.id}
              type="button"
              onClick={() => setOpen(ind)}
              className="text-left transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <Card className="h-full border-border/80 hover:shadow-card-hover">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base leading-snug">{ind.title}</CardTitle>
                  <p className="text-xs text-muted-foreground line-clamp-2">{ind.shortDescription}</p>
                </CardHeader>
                <CardContent className="space-y-2 pt-0">
                  {ind.kpis.map((k) => (
                    <div key={k.label} className="flex items-baseline justify-between gap-2 border-t border-border/60 pt-2 first:border-t-0 first:pt-0">
                      <span className="text-xs text-muted-foreground">{k.label}</span>
                      <span className="text-sm font-semibold tabular-nums text-foreground">{k.value}</span>
                    </div>
                  ))}
                  <span className="inline-block pt-1 text-xs font-medium text-amber-700 dark:text-amber-400">Ver análise</span>
                </CardContent>
              </Card>
            </button>
          ))}
        </div>
      </div>

      <Modal
        isOpen={Boolean(open)}
        onClose={() => setOpen(null)}
        title={open?.title ?? 'Indicador'}
        size="xl"
      >
        {open ? (
          <div className="space-y-4">
            <DiretoriaChart indicator={open} />
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-left">
                    {open.tableHeaders.map((h) => (
                      <th key={h} className="px-3 py-2 font-medium">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {open.tableRows.map((row, ri) => (
                    <tr key={ri} className="border-b border-border/80 last:border-0">
                      {row.map((cell, ci) => (
                        <td key={ci} className="px-3 py-2 text-foreground">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">{open.footnote}</p>
            {open.id === 'ia-zeh' ? (
              <div className="flex flex-wrap gap-2 border-t border-border pt-3">
                <Link
                  to="/whatsapp-integration"
                  className={cn(
                    'inline-flex h-8 items-center justify-center rounded-button border border-input bg-background px-3 text-body-sm font-medium shadow-soft-sm',
                    'hover:bg-muted hover:border-input hover:shadow-soft-md'
                  )}
                >
                  Abrir integrações WhatsApp
                </Link>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  );
};
